// src/components/SessionGuard.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { validateToken, getSession, createSession, getUserProfile } from "../api/propertiesApi";
import { API_URL } from "../api/client";

// normaliza (quita acentos) para comparar estados
const norm = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// ¿La sesión pertenece al usuario?
const isOwner = (sessionData, myId) => {
  const ownerId =
    sessionData?.user_id ??
    sessionData?.owner_id ??
    sessionData?.owner_user_id ??
    sessionData?.created_by_id ??
    sessionData?.created_by ??
    sessionData?.user?.id ??
    sessionData?.owner?.id ??
    sessionData?.creator?.id ??
    null;

  // Si el backend no devuelve owner, no bloqueamos por owner
  if (!ownerId) return true;
  if (!myId) return false;
  return String(ownerId) === String(myId);
};

// ¿La sesión está activa?
const isActive = (sessionData) => {
  // flags booleanos
  const completed =
    Boolean(sessionData?.is_completed) ||
    Boolean(sessionData?.completed) ||
    Boolean(sessionData?.done);
  const archived =
    Boolean(sessionData?.archived) || Boolean(sessionData?.is_archived);
  const cancelled =
    Boolean(sessionData?.cancelled) || Boolean(sessionData?.is_cancelled);

  if (completed || archived || cancelled) return false;

  // status textual (ES y EN)
  const s = norm(sessionData?.status || sessionData?.state || "");
  const deadStatuses = new Set([
    "completado", "completada",
    "finalizado", "finalizada",
    "cerrado", "cerrada",
    "cancelado", "cancelada",
    "archivado", "archivada",
    "completed", "complete", "closed", "cancelled", "archived", "done",
  ]);
  if (deadStatuses.has(s)) return false;

  // estados que consideramos VIVOS explícitamente
  const liveStatuses = new Set([
    "en_proceso", "en proceso", "en-proceso",
    "activo", "activa",
    "abierto", "abierta",
    "open", "in_progress", "in-progress",
  ]);
  if (liveStatuses.has(s)) return true;

  // expiración opcional
  const exp = sessionData?.expires_at || sessionData?.valid_until;
  if (exp) {
    const dt = new Date(exp);
    if (!Number.isNaN(dt.getTime()) && dt < new Date()) return false;
  }

  // por defecto: si no está explícitamente cerrada ni marcada completed, la damos por activa
  return true;
};

export default function SessionGuard({ children }) {
  const [checking, setChecking] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // 1) token válido
        const ok = await validateToken();
        if (!ok) {
          if (!cancelled) navigate("/", { replace: true });
          return;
        }

        // 2) obtener mi user_id
        let myId;
        try {
          const profile = await getUserProfile();
          myId =
            profile?.id ??
            profile?.user_id ??
            profile?.uid ??
            profile?.user?.id ??
            null;
          if (!myId) {
            const r = await fetch(`${API_URL}/validate-token`, { credentials: "include" });
            if (r.ok) {
              const j = await r.json();
              myId = j?.user_id;
            }
          }
        } catch {}
        if (!myId) {
          if (!cancelled) navigate("/", { replace: true });
          return;
        }

        // 3) buscar sesión actual en este tab
        let sid = sessionStorage.getItem("current_session");

        // 3.a) si hay resume_session (entrás por link), validarla y tomarla
        if (!sid) {
          const resume = sessionStorage.getItem("resume_session");
          if (resume) {
            const s = await getSession(resume);
            if (!isOwner(s, myId) || !isActive(s)) {
              sessionStorage.removeItem("resume_session");
              if (!cancelled) navigate("/", { replace: true });
              return;
            }
            sessionStorage.setItem("current_session", String(resume));
            sessionStorage.removeItem("resume_session");
            sid = String(resume);
          }
        }

        // 3.b) si venís del wizard con datos, crear sesión y usarla
        if (!sid && location.state?.propertyData) {
          const created = await createSession(location.state.propertyData);
          const newId = created?.session_id ?? created?.id;
          if (!newId) {
            if (!cancelled) navigate("/", { replace: true });
            return;
          }
          sessionStorage.setItem("current_session", String(newId));
          sid = String(newId);
        }

        // 3.c) si no hay sesión -> afuera
        if (!sid) {
          if (!cancelled) navigate("/", { replace: true });
          return;
        }

        // 4) validar la sesión actual (owner + activa)
        const sess = await getSession(sid);
        const own = isOwner(sess, myId);
        const active = isActive(sess);

        // DEBUG útil mientras lo ajustás:
        // console.log({ myId, sid, sess_status: sess?.status, is_completed: sess?.is_completed, own, active });

        if (!own || !active) {
          sessionStorage.removeItem("current_session");
          if (!cancelled) navigate("/", { replace: true });
          return;
        }

        if (!cancelled) setChecking(false);
      } catch (e) {
        // console.error("SessionGuard error:", e);
        if (!cancelled) navigate("/", { replace: true });
      }
    })();

    return () => { cancelled = true; };
  }, [location, navigate]);

  if (checking) {
    return (
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',fontFamily:'Inter,sans-serif'}}>
        Verificando tu sesión…
      </div>
    );
  }

  return children;
}
